/**
 * The next meeting (TMMAPP-0152 §B 12; TMMAPP-0159 §B 6): one rule and one hook, shared by Home,
 * My Money Meetings and the sidebar's Today block.
 *
 * - **The rule** (`nextMeetingOf`): the next SCHEDULED or live occurrence that hasn't ended; ties
 *   go to the earliest `startsAt`.
 * - **The read** (`useNextMeeting`, the Today block's): every meeting the person is in
 *   (`GET /v1/me/meetings`), over the window the caller names.
 * - **The link** (`meetingSessionPath`): the meeting's own session, where a meeting runs (start,
 *   notes, items, end, summary); with no meeting, My Money Meetings.
 */
import { useEffect, useState } from 'react';
import { meetingsApi, type MeetingCalendarEntry } from '@/api/meetings';
import { sessionPathOf } from './standalone';

/** The next SCHEDULED or live occurrence that hasn't ended; ties go to the earliest start. */
export function nextMeetingOf(
  entries: readonly MeetingCalendarEntry[],
  now: number,
): MeetingCalendarEntry | null {
  const ends = (entry: MeetingCalendarEntry) => Date.parse(entry.endsAt);
  const open = entries.filter(
    (entry) => (entry.live || entry.status === 'SCHEDULED') && (entry.live || !(ends(entry) < now)),
  );
  return open.slice().sort((a, b) => a.startsAt.localeCompare(b.startsAt))[0] ?? null;
}

/**
 * The meeting's session (`/v/{va}/meetings/{mid}/{occ}`, or `/me/meetings/{mid}/{occ}` for a
 * standalone meeting, TMMAPP-0159 §D 22), else My Money Meetings.
 */
export function meetingSessionPath(
  entry: Pick<MeetingCalendarEntry, 'virtualAccountId' | 'meetingId' | 'occurrenceKey'> | null,
): string {
  if (entry === null) return '/me/meetings';
  return sessionPathOf(entry.virtualAccountId, entry.meetingId, entry.occurrenceKey);
}

export interface NextMeeting {
  /** `undefined` while it loads; `null` when nothing is scheduled, or when the read failed. */
  entry: MeetingCalendarEntry | null | undefined;
}

/**
 * The next meeting across every meeting the person is in, over `window` (civil dates). A change of
 * `reloadKey` reads again.
 */
export function useNextMeeting({
  window,
  reloadKey = null,
}: {
  window: { from: string; to: string };
  reloadKey?: string | null;
}): NextMeeting {
  const key = `${window.from}|${window.to}`;
  const [state, setState] = useState<{
    key: string;
    entry: MeetingCalendarEntry | null | undefined;
  }>({ key, entry: undefined });
  const { from, to } = window;

  useEffect(() => {
    let cancelled = false;
    meetingsApi.listMine({ from, to }).then(
      (calendar) => {
        if (!cancelled) setState({ key, entry: nextMeetingOf(calendar.items, Date.now()) });
      },
      () => {
        if (!cancelled) setState({ key, entry: null });
      },
    );
    return () => {
      cancelled = true;
    };
  }, [key, from, to, reloadKey]);

  // A new window reads afresh: until it answers, the entry is loading.
  if (state.key !== key) return { entry: undefined };
  return { entry: state.entry };
}
